import React, { type ReactNode, useCallback, useRef } from 'react';
import { xcss, Pressable } from '@atlaskit/primitives';
import { asyncCreateIssueTypeModalEntryPoint } from '@atlassian/jira-create-issue-type-modal/entrypoint.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ModalTrigger } from '@atlassian/entry-point-modal-trigger';
import type { ConfigureSource } from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import type { UFOExperience } from '@atlassian/ufo';
import { defaultModalProps } from '@atlassian/jira-issue-type-config-common/src/constants.tsx';
// eslint-disable-next-line jira/deprecations/ban-identifiers
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { FormDialogSkeleton } from '@atlassian/jira-form-dialog-skeleton/src/ui/FormDialogSkeleton.tsx';
import { Trigger, useTriggerContext } from '@atlassian/entry-point-panel';
import { ConfigureWorkTypePanel } from '../ConfigureWorkTypePanel.tsx';

type CreateWorkTypeButtonProps = {
	children: ReactNode;
	onClick: () => void;
	entryPointProps: {
		onIssueTypeCreated: () => void;
		issueTypesCountLimit: number;
		onClose: () => void;
		onOpen: () => void;
		source: ConfigureSource;
		ufoExperience: UFOExperience;
	};
	entryPointParams: {
		cloudId: string;
		projectKey: string;
		shouldFetchProjectData: boolean;
	};
	shouldTriggerModal: boolean;
};

export const CreateWorkTypeButton = ({
	children,
	onClick,
	entryPointProps,
	entryPointParams,
	shouldTriggerModal,
}: CreateWorkTypeButtonProps) => {
	const popupTrigger = useTriggerContext(ConfigureWorkTypePanel);
	const createWorkTypeButtonRef = useRef<HTMLElement>(null);

	const handleClick = useCallback(() => {
		onClick();
	}, [onClick]);

	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [inlineIssueTypeConfigProductionizationExpConfig] = UNSAFE_noExposureExp(
		'inline_issue_type_config_productionization_exp',
	);

	const getClickElement = (ref: React.RefObject<HTMLElement>) => (
		<Pressable
			// onClick is intercepted by ModalEntryPointPressableTrigger/Trigger component via usePressablePreloadRef,
			// so we need to manually trigger a synthetic click on left mouse click
			onMouseDown={(e) => {
				// Left mouse button was clicked
				if (e.button === 0) {
					createWorkTypeButtonRef?.current?.click();
				}
			}}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					createWorkTypeButtonRef?.current?.click();
				}
			}}
			onClick={handleClick}
			xcss={createWorkTypeInlineNoResultStyles}
			// @ts-expect-error - The reference type from ModalEntryPointPressableTrigger is incompatible with Pressable,
			// but this works correctly at runtime and is a type-system limitation
			ref={ref}
		>
			{children}
		</Pressable>
	);

	// eslint-disable-next-line no-nested-ternary
	return shouldTriggerModal ? (
		inlineIssueTypeConfigProductionizationExpConfig.get('isEnabled', false) &&
		fg('inline_config_migrate_deprecated_modal_entrypoints') ? (
			<ModalTrigger
				entryPoint={asyncCreateIssueTypeModalEntryPoint}
				entryPointProps={entryPointProps}
				entryPointParams={entryPointParams}
				modalProps={defaultModalProps}
				Fallback={() => <FormDialogSkeleton numFormFields={1} />}
			>
				{({ ref }) => getClickElement(ref)}
			</ModalTrigger>
		) : (
			<ModalEntryPointPressableTrigger
				entryPoint={asyncCreateIssueTypeModalEntryPoint}
				entryPointProps={entryPointProps}
				entryPointParams={entryPointParams}
				interactionName="create-issue-type-modal"
				modalProps={defaultModalProps}
				useInternalModal={inlineIssueTypeConfigProductionizationExpConfig.get('isEnabled', false)}
				Fallback={() => <FormDialogSkeleton numFormFields={1} />}
			>
				{({ ref }) => getClickElement(ref)}
			</ModalEntryPointPressableTrigger>
		)
	) : (
		<Trigger
			trigger={popupTrigger}
			entryPoint={asyncCreateIssueTypeModalEntryPoint}
			entryPointProps={entryPointProps}
			entryPointParams={entryPointParams}
			fallback={<FormDialogSkeleton numFormFields={1} />}
			forwardedRef={createWorkTypeButtonRef}
		>
			{({ ref }) => getClickElement(ref)}
		</Trigger>
	);
};

const createWorkTypeInlineNoResultStyles = xcss({
	paddingBlockStart: 'space.0',
	paddingInline: 'space.0',
	marginTop: 'space.100',
	borderRadius: 'radius.xsmall',
	color: 'color.link',
	backgroundColor: 'color.background.neutral.subtle',
	display: 'block',
	fontWeight: 'font.weight.bold',
});
